/* eslint-disable complexity, react/no-unstable-nested-components,  @next/next/no-img-element, sonarjs/cognitive-complexity */
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import React, { useState, useContext } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useSelector } from 'react-redux';
import { Element } from 'react-scroll';
import { SCROLL_CONSTANTS } from '../../../../brands/default/constants';
import { SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL } from '../../../../brands/default/content/components/deal';
import { WISHLIST } from '../../../../brands/default/features';
import { isLightPage } from '../../../../helpers/browser';
import {
  arePricesDifferent,
  getColumn,
  getImageForVariant,
  getRow,
} from '../../../../helpers/chooseYourOptions';
import { formatCurrency } from '../../../../helpers/currency';
import {
  getModalSocialCue,
  useSocialCues,
  isDepositDeal,
  usePins,
  showPostage,
} from '../../../../helpers/deals';
import { replaceImageServer } from '../../../../helpers/image';
import { getBackendSocialCueMessage } from '../../../../helpers/socialCues';
import ThemeContext from '../../../../providers/ThemeProvider';
import Message from '../../../_generic/Message';
import PrimaryButton from '../../../_generic/button/PrimaryButton';
import RadioButtonWithLabel from '../../../_generic/radio-button/RadioButtonWithLabel';
import WowMap from '../../../_generic/wow-map/WowMap';
import ListMainDeal from '../../ListMainDeal';
import MainDealBoughtCount from '../../MainDealBoughtCount';
import MainDealCarousel from '../../MainDealCarousel';
import MainDealFinePrint from '../../MainDealFinePrint';
import MainDealFullDetails from '../../MainDealFullDetails';
import SocialCueMainDeal from '../../SocialCueMainDeal';
import CollapsableSection from '../CollapsableSection';
import DealPriceSplat from '../DealPriceSplat';
import PostageAndPackagingText from '../PostageAndPackagingText';
import VatText from '../VatText';
import GiftingButton from '../gifting-button/GiftingButton';
import DealBuyButton from './DealBuyButton';
import DealGiftButton from './DealGiftButton';

const WishlistIcon = dynamic(() => import('../WishlistIcon'), {
  ssr: false,
});
/**
 * see https://wowcher.atlassian.net/wiki/spaces/WOWA/pages/2266005505/Choose+Your+Options
 */

export const layoutOptions = {
  travel: 'travel',
};

const SecondCheckoutChooseYourOptionsModal = ({
  buttonRef = undefined,
  deal = {},
  finePrintCollapsed,
  fullDetailsCollapsed,
  handleBuyButtonClick,
  initialColumnSel = 0,
  isOptionSelected = () => {},
  layout,
  locationCollapsed,
  mapView = false,
  secondCheckout = false,
  setFinePrintCollapsed,
  setFullDetailsCollapsed,
  setLocationCollapsed,
}) => {
  const [rowSelection, setRowSelection] = useState(0); // 1st option
  const [columnSelection, setColumnSelection] = useState(initialColumnSel); // 2nd Option (hidden if only 1 option)
  const [selectedOption, setSelectedOption] = useState(-1);
  const [payDeposit, setPayDeposit] = useState(deal?.depositAvailable || false); // deposit or payment in full
  const [showEmptyMessage, setShowEmptyMessage] = useState(false);
  const theme = useContext(ThemeContext);
  const socialCuesData = useSocialCues(deal.id);
  const row = getRow(deal);
  const [column, setColumn] = useState(() => getColumn(deal, rowSelection));
  const isLayoutTravel = Boolean(layout === layoutOptions.travel);
  const ssr = useSelector((state) => state.deals.ssr);
  const pins = usePins(deal);
  const hasPostage = showPostage(deal, true, true);

  const onRowChange = (rowId) => {
    const id = Number.parseInt(rowId, 10);
    const col = getColumn(deal, id);
    setRowSelection(id);
    setColumn(col);
    const colSelection = col.items.length === 1 ? col.items[0].id : 0;

    setColumnSelection(colSelection);

    const selection = deal.products.findIndex(
      (item) => item.rowId === id && item.columnId === colSelection,
    );
    setSelectedOption(selection);
    setShowEmptyMessage(false);
    if (column.items.length === 1) isOptionSelected(true);
  };

  const onSecondRowChange = (colId) => {
    const secondValue = Number.parseInt(colId, 10);
    setColumnSelection(secondValue);

    if (deal.depositAvailable) {
      setPayDeposit(Boolean(secondValue));
    }
    const selection = deal.products.findIndex(
      (item) => item.rowId === rowSelection && item.columnId === secondValue,
    );
    setSelectedOption(selection);
    setShowEmptyMessage(false);
    isOptionSelected(true);
  };

  const showError = () => {
    setShowEmptyMessage(true);
  };

  const DepositMessage = () => {
    if (!isDepositDeal(deal)) {
      return null;
    }

    return (
      <Message
        extraClasses="message-center message-border-radius green-theme"
        html
        message={SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL.depositMessage.replace(
          '%price',
          formatCurrency(deal?.currency, deal?.depositPrice),
        )}
        showIcon={false}
      />
    );
  };

  const getGiftButton = (itemSelected) => {
    if (!deal.giftable) return null;
    if (itemSelected < 0)
      return (
        <div className="gift-button">
          <PrimaryButton
            aria-label={
              SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL.giveAsAGiftButton
            }
            className="full-width outline margin"
            onClick={() => showError()}
          >
            {SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL.giveAsAGiftButton}
          </PrimaryButton>
        </div>
      );

    return (
      <div className="dealgift-button">
        <DealGiftButton
          className="full-width outline margin"
          deal={deal}
          position={selectedOption}
        />
      </div>
    );
  };
  const imageVariant = getImageForVariant(deal, rowSelection, columnSelection);
  const selectBox = ({ action, data, htmlId, value }) => {
    return (
      <>
        <Dropdown drop="up" flip={false}>
          <Dropdown.Toggle className="dropdown-outline" id={htmlId}>
            {value
              ? data.items.find((x) => x.id === value).displayAs
              : `${SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL.pleaseChoose} ${data.label}`}
          </Dropdown.Toggle>

          <Dropdown.Menu className="full-width-list" id="menu-list">
            <Dropdown.Item
              className="full-width-list"
              onClick={() => action(0)}
            >
              {SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL.pleaseChoose}{' '}
              {data.label}
            </Dropdown.Item>

            {data.items.map((item) => {
              if ((isLightPage() && !item.disabled) || !isLightPage())
                return (
                  <Dropdown.Item
                    disabled={item.disabled}
                    key={item.id}
                    onClick={() => action(item.id)}
                    value={item.id}
                  >
                    {item.isBestseller ? (
                      <div className="green-text text-wrap">
                        {item.displayAs} -{' '}
                        {SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL.bestseller}
                      </div>
                    ) : (
                      <div className="text-wrap">{item.displayAs}</div>
                    )}
                  </Dropdown.Item>
                );

              return null;
            })}
          </Dropdown.Menu>
        </Dropdown>

        <style jsx>
          {`
            :global(.dropdown-outline) {
              display: inline;
              width: 100%;
              background-color: transparent;
              color: ${theme.commoncolors.greycharcoal};
              border: 1px solid ${theme.commoncolors.inputiconcolor};
              font-size: ${mapView ? '16px' : '14px'};
              min-width: 50px;
              min-height: 28px;
              border-radius: 5px;
              padding: ${mapView ? '4px 10px 4px 8px' : '0 10px 0 8px'};
              appearance: none;
              line-height: ${mapView ? '1.4em' : '1.2em'};
              margin-bottom: 10px;
              text-align: left;
            }
            :global(.dropdown-outline:hover) {
              background-color: transparent;
              color: ${theme.commoncolors.greycharcoal};
              border: 1px solid ${theme.commoncolors.inputiconcolor};
            }
            :global(.dropdown-outline.dropdown-toggle) {
              background-color: transparent !important;
              color: ${theme.commoncolors.greycharcoal} !important;
              border: 1px solid ${theme.commoncolors.inputiconcolor} !important;
            }
            :global(.dropdown-outline::after) {
              float: right;
              margin-top: 6px;
            }
            :global(.full-width-list) {
              width: 100%;
              font-size: 13px;
            }
            .green-text {
              color: ${theme.commoncolors.assure};
            }
            .text-wrap {
              white-space: normal;
            }
          `}
        </style>
      </>
    );
  };

  const ProceedButton = () => {
    return (
      <>
        {selectedOption < 0 && (
          <PrimaryButton
            aria-label={SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL.buyNow}
            className="size-larger full-width margin modal-sticky"
            onClick={showError}
          >
            {SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL.buyNow}
          </PrimaryButton>
        )}
      </>
    );
  };

  const ChooseYourOptionsInputs = () => {
    return (
      <div
        className="choose-your-options__inputs"
        style={{
          backgroundColor: 'white',
          bottom: '40px',
          padding: '5px 0 20px 0',
          position: 'sticky',
        }}
      >
        {/* ROW SELECTION */}
        {selectBox({
          action: onRowChange,
          data: row,
          htmlId: 'rowId',
          value: rowSelection,
        })}

        {/* COLUMN SELECTION (HIDDEN IF ONLY 1 OPTION) */}
        {column.items.length > 1 &&
          selectBox({
            action: onSecondRowChange,
            data: column,
            htmlId: 'columnId',
            value: columnSelection,
          })}
        <div
          className={`deal-product-selector__empty-message ${
            showEmptyMessage ? 'visible ' : 'hidden'
          }`}
        >
          {SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL.pleaseChooseOneOption}
        </div>
      </div>
    );
  };

  const PayAmount = () => {
    if (!isDepositDeal(deal)) return false;

    return columnSelection ? (
      <div className="cyo-deposit">
        <RadioButtonWithLabel
          checked={payDeposit}
          className={classNames('radio-button--boxed radio-button--switch', {
            'radio-button--green': payDeposit,
          })}
          label={
            <>
              <p className="label-text">
                {SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL.payDeposit}
              </p>
              <p className="label-price">
                {formatCurrency(deal?.currency, deal?.depositPrice)}
              </p>
            </>
          }
          onChange={() => setPayDeposit(true)}
        />
        <RadioButtonWithLabel
          checked={!payDeposit}
          className={classNames('radio-button--boxed radio-button--switch', {
            'radio-button--green': !payDeposit,
          })}
          label={
            <>
              <p className="label-text">
                {SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL.payFullDeposit}
              </p>
              <p className="label-price">
                {formatCurrency(deal?.currency, deal?.price)}
              </p>
            </>
          }
          onChange={() => setPayDeposit(false)}
        />
      </div>
    ) : null;
  };

  const SocialCues = () => {
    return (
      <div className="choose-your-options__cue">
        <SocialCueMainDeal
          className="main-deal-social-cue--assure main-deal-social-cue--no-margin"
          cue={
            deal.display?.bought && deal.totalBought === 0
              ? deal.newDealMessageTag
              : null
          }
        />
        <SocialCueMainDeal
          className="main-deal-social-cue--alert"
          cue={getModalSocialCue({
            deal,
            selected: selectedOption,
          })}
        />
        <SocialCueMainDeal
          className="main-deal-social-cue--assure"
          cue={getBackendSocialCueMessage(socialCuesData)}
        />
      </div>
    );
  };

  return (
    <>
      {isLayoutTravel && (
        <div className="cyo-block">
          <div className="cyo-header">
            {SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL.chooseYourOptions}
          </div>
          <div className="cyo-body">
            <DepositMessage />
            <div className="cyo-options">{ChooseYourOptionsInputs()}</div>
            <SocialCues />
            <div className="cyo-buttons">
              <ProceedButton />
              {selectedOption >= 0 && (
                <DealBuyButton
                  buttonRef={buttonRef}
                  className={
                    'size-larger deal-main-deal__buy full-width margin modal-sticky'
                  }
                  deal={deal}
                  handleBuyButtonClick={handleBuyButtonClick}
                  mapView={mapView}
                  payDeposit={payDeposit}
                  position={selectedOption}
                  text={SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL.buyNow}
                  trackingEnabled={false}
                />
              )}
              {getGiftButton(selectedOption)}
            </div>
          </div>
        </div>
      )}

      {!isLayoutTravel && (
        <>
          {!isDepositDeal(deal) && (
            <div className="choose-your-options__image-container">
              {mapView && (
                <MainDealCarousel
                  deal={deal}
                  disableClickAction
                  isClosed={false}
                  onClick={() => {}}
                />
              )}
              {!mapView && (
                <>
                  {imageVariant ? (
                    <img
                      alt={deal.headline}
                      className="choose-your-options__image"
                      src={replaceImageServer({
                        dealId: deal.id,
                        image: imageVariant,
                      })}
                    />
                  ) : (
                    <img
                      alt={deal.headline}
                      className="choose-your-options__image"
                      src={replaceImageServer({
                        dealId: deal.id,
                        image: deal?.images[0],
                      })}
                    />
                  )}
                </>
              )}
              <DealPriceSplat
                className="bottom-right"
                deal={deal}
                discount={!mapView}
                secondCheckout
              />
              {mapView && WISHLIST && <WishlistIcon dealId={deal.id} />}
            </div>
          )}
          <h2 className="deal-headline">{deal.headline}</h2>
          {!mapView && (
            <PostageAndPackagingText
              className="mobile-invert"
              deal={deal}
              isMain
              isSmall
              secondCheckout
              show={hasPostage}
            />
          )}
          <SocialCues />
          <MainDealBoughtCount deal={deal} />
          <div className="deal-info">
            {deal?.discount > 0 && deal?.display?.discount && (
              <p className="money-item">
                <span className="label">
                  {SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL.wasText}
                </span>
                <span className="value-strike">
                  {formatCurrency(
                    deal?.currency,
                    deal?.originalPrice,
                    false,
                    true,
                  )}
                </span>
              </p>
            )}
            <p className="money-item">
              <span className="label">
                {arePricesDifferent(deal)
                  ? SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL.from
                  : SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL.now}
              </span>
              <span className="value">
                {formatCurrency(deal?.currency, deal.price, false, true)}
              </span>
            </p>
          </div>
          <GiftingButton deal={deal} isEvergreen={false} isMobile />
          <p
            className={classNames('deal-main-deal__subtitle', {
              visible: !ssr,
            })}
          >
            {deal?.title}
          </p>
          <ListMainDeal
            className="highlights"
            list={deal.highlights}
            title={SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL.productFeatures}
          />
          <MainDealFullDetails
            collapsed={fullDetailsCollapsed}
            deal={deal}
            onToggle={() => setFullDetailsCollapsed((state) => !state)}
          />
          {!mapView && pins && pins.length > 0 && (
            <>
              <Element name={SCROLL_CONSTANTS.SCROLL_TO_MAP_LOCATION} />
              {!isLightPage() && (
                <CollapsableSection
                  collapsed={locationCollapsed}
                  onToggle={() => setLocationCollapsed((state) => !state)}
                  title={SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL.location}
                >
                  <WowMap
                    maxZoom={15}
                    pins={pins}
                    zoom={deal?.mapZoomDefaultSize}
                  />
                </CollapsableSection>
              )}
            </>
          )}
          <MainDealFinePrint
            collapsed={finePrintCollapsed}
            deal={deal}
            onToggle={() => setFinePrintCollapsed((state) => !state)}
          />
          <DepositMessage />
          <PayAmount />
          <div className="deal-main-deal__vat-text--mobile">
            <VatText deal={deal} />
          </div>
          {ChooseYourOptionsInputs()}
          <ProceedButton />
          {selectedOption >= 0 && (
            <DealBuyButton
              className="size-larger deal-main-deal__buy full-width margin modal-sticky"
              deal={deal}
              handleBuyButtonClick={handleBuyButtonClick}
              mapView={mapView}
              payDeposit={payDeposit}
              position={selectedOption}
              secondCheckout={secondCheckout}
              text={SECOND_CHECKOUT_CHOOSE_YOUR_OPTIONS_MODAL.buyNow}
              trackingEnabled={false}
            />
          )}
        </>
      )}

      <style jsx>{`
        .cyo-header {
          background-color: ${theme.colors.primary};
          min-height: 50px;
          display: flex;
          flex-direction: column;
          justify-content: space-around;
          text-align: center;
          font-size: 20px;
          color: ${theme.colors.textonprimary};
          text-transform: capitalize;
        }
        .money-item {
          line-height: 1em;
          margin-bottom: 0;
          min-width: 35px;
          display: flex;
          gap: 0.5rem;
          flex-direction: column;
        }
        .label {
          text-transform: uppercase;
          color: black;
          font-size: 12px;
        }
        .deal-headline {
          width: 80%;
        }
        .deal-main-deal__bought {
          color: ${theme.colors.primary};
          font-weight: bold;
          padding: 12px 0;
          margin: 0;
          font-size: 20px;
        }
        .value-strike {
          font-size: 20px;
          color: ${theme.colors.primary};
          padding: 0 4px;
          margin-left: -4px;
          background: linear-gradient(
            to left top,
            transparent 47.75%,
            currentColor 49.5%,
            currentColor 50.5%,
            transparent 52.25%
          );
        }
        .deal-info {
          padding: 10px 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-grow: 1;
          width: 40%;
          text-decoration: none;
        }
        .value {
          font-size: 20px;
          color: ${theme.colors.primary};
        }
        .cyo-body {
          display: flex;
          flex-direction: column;
          border: 4px solid ${theme.colors.primary};
          border-top: none;
          padding: 20px;
          margin-bottom: 0;
        }
        .cyo-buttons {
          padding-top: 10px;
        }
        .cyo-block {
          margin-bottom: 20px;
        }
        :global(.cyo-deposit) {
          display: flex;
          padding: 5px 0px 10px 0px;
        }
        @media (max-width: ${theme.breakpoints.smDown}) {
          :global(.cyo-deposit) {
            padding-bottom: 10px;
          }
          .deal-info {
            width: 50%;
          }
        }
        :global(.cyo-deposit label) {
          flex-grow: 1;
        }
        :global(.cyo-deposit label .label-text),
        :global(.cyo-deposit label .label-price) {
          margin: 0px;
        }
        :global(.cyo-deposit label:first-child) {
          margin-right: 10px;
        }

        :global(.choose-your-options__cue) {
          overflow: hidden;
        }
        @media (max-width: ${theme.breakpoints.smDown}) {
          :global(.choose-your-options__cue) {
            padding-top: 10px;
          }
        }
        :global(.choose-your-options__discount) {
          float: right;
          padding: 0 0 10px 10px;
        }
        .choose-your-options__image-container {
          padding: 0px 0px 20px 0px;
          position: relative;
          margin-top: -10px;
        }
        :global(.choose-your-options__image-container .thumbs) {
          display: none;
        }
        .choose-your-options__image {
          width: 100%;
        }
        .choose-your-options__inputs {
          padding: 0px 0px 12px 0px;
        }
        :global(.deal-product-selector__empty-message) {
          margin: 0 auto 20px;
          padding: 6px 12px;
          color: ${theme.commoncolors.alert};
          border: 4px solid ${theme.commoncolors.alert};
          border-radius: 4px;
          text-align: center;
        }
        :global(.deal-product-selector__empty-message.hidden) {
          display: none;
        }
        :global(.choose-your-options .modal-body) {
          padding: 0 20px 20px 20px;
        }
        :global(.choose-your-options__discount .deal-main-deal__discount) {
          background: ${theme.colors.primary};
        }
        :global(.choose-your-options .modal-content) {
          border: 1px solid ${theme.colors.primary};
        }
        :global(.size-larger) {
          position: ${layoutOptions.travel ? 'static' : 'sticky'};
          bottom: 0px;
          z-index: 1001;
        }
      `}</style>
    </>
  );
};

SecondCheckoutChooseYourOptionsModal.propTypes = {
  buttonRef: PropTypes.object,
  deal: PropTypes.object,
  finePrintCollapsed: PropTypes.bool,
  fullDetailsCollapsed: PropTypes.bool,
  handleBuyButtonClick: PropTypes.func,
  initialColumnSel: PropTypes.number,
  isOptionSelected: PropTypes.func,
  layout: PropTypes.string,
  locationCollapsed: PropTypes.bool,
  mapView: PropTypes.bool,
  secondCheckout: PropTypes.bool,
  setFinePrintCollapsed: PropTypes.func,
  setFullDetailsCollapsed: PropTypes.func,
  setLocationCollapsed: PropTypes.func,
};

export default SecondCheckoutChooseYourOptionsModal;
